import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Drop-in replacement for `React.lazy` that auto-recovers from stale-chunk
 * errors after a deploy.
 *
 * When a new build ships, Vite emits asset filenames with new content hashes
 * (`OrderDetailPage-<hash>.js`). A tab that loaded the *old* `index.html`
 * still references the old hashes; navigating to a lazy route then fails with
 * `TypeError: Failed to fetch dynamically imported module`. The page hangs
 * (Suspense never resolves) or trips the ErrorBoundary.
 *
 * This wrapper catches that failure and forces a **single** full reload, which
 * pulls the fresh (no-cache) `index.html` and the new chunk names. A flag in
 * `sessionStorage`, keyed per chunk, guarantees we reload at most once per
 * chunk — so a genuinely missing chunk (broken build, offline) surfaces the
 * real error instead of looping forever.
 *
 * Usage — identical to `lazy`:
 *   const OrderDetailPage = lazyWithReload(() => import('./pages/OrderDetailPage'));
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own `ComponentType<any>` constraint
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  /** Stable key for the reload guard. Defaults to the factory source string. */
  chunkKey?: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const key = `chunk-reload:${chunkKey ?? factory.toString()}`;
    try {
      const mod = await factory();
      // Success — clear any stale guard so a future failure can reload again.
      sessionStorage.removeItem(key);
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so Suspense keeps the fallback
        // visible during the (imminent) reload instead of flashing an error.
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}

/**
 * True when the error looks like a failed dynamic-import / chunk fetch.
 * Covers Chrome, Firefox and Safari phrasings plus Vite's preload error.
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /'?text\/html'? is not a valid JavaScript MIME type/i.test(msg)
  );
}
